'use client';

import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { ToastVariant } from '@/lib/toast/types';
import { cn } from '@/lib/utils';

// アプリ全体で使うトースト。保存・削除などのアクション後の成功/失敗を
// 画面上部中央にフロート表示する。NoticeBanner と同じ塗り (bg-*-50 /
// text-*-900) で意味を出し、アイコン + 影 + 自動消滅を足したもの。
//
// 使い方:
//   const { toast } = useToast();
//   toast.success('保存しました');
//   toast.error('保存に失敗しました');
// フォーム (useActionState) からは useActionStateToast(state) が便利。

type ToastItem = {
  id: number;
  message: string;
  variant: ToastVariant;
  // 退場アニメーション中フラグ。true の間だけ animate-out を当てる。
  leaving: boolean;
};

type ToastOptions = {
  variant?: ToastVariant;
  // 表示時間 (ms)。省略時は variant で決める (error は長め)。
  duration?: number;
};

type ToastFn = ((message: string, options?: ToastOptions) => void) & {
  success: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
  error: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
  info: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
  warn: (message: string, options?: Omit<ToastOptions, 'variant'>) => void;
};

type ToastContextValue = {
  toast: ToastFn;
};

const ToastContext = createContext<ToastContextValue | null>(null);

// 退場アニメーションの尺 (globals の tw-animate-css と揃える)。
const LEAVE_MS = 180;

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warn: 5000,
  // 失敗は読む時間を長めに取る。
  error: 6000,
};

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success: 'bg-emerald-50 text-emerald-900',
  error: 'bg-red-50 text-red-900',
  info: 'bg-sky-50 text-sky-900',
  warn: 'bg-amber-50 text-amber-900',
};

const VARIANT_ICON: Record<ToastVariant, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warn: AlertTriangle,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);
  // 消去タイマーを id ごとに保持し、アンマウント時に掃除する。
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const remove = useCallback((id: number) => {
    // まず leaving を立てて退場アニメーションを見せ、その後 DOM から外す。
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    const t = setTimeout(() => {
      setItems((prev) => prev.filter((it) => it.id !== id));
      timers.current.delete(id);
    }, LEAVE_MS);
    timers.current.set(id, t);
  }, []);

  const push = useCallback(
    (message: string, options?: ToastOptions) => {
      const trimmed = message?.trim();
      if (!trimmed) return;
      const variant = options?.variant ?? 'success';
      const id = ++idRef.current;
      setItems((prev) => [...prev, { id, message: trimmed, variant, leaving: false }]);
      const duration = options?.duration ?? DEFAULT_DURATION[variant];
      const t = setTimeout(() => remove(id), duration);
      timers.current.set(id, t);
    },
    [remove],
  );

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const toast = useMemo<ToastFn>(() => {
    const fn = ((message: string, options?: ToastOptions) => push(message, options)) as ToastFn;
    fn.success = (message, options) => push(message, { ...options, variant: 'success' });
    fn.error = (message, options) => push(message, { ...options, variant: 'error' });
    fn.info = (message, options) => push(message, { ...options, variant: 'info' });
    fn.warn = (message, options) => push(message, { ...options, variant: 'warn' });
    return fn;
  }, [push]);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster items={items} onDismiss={remove} />
    </ToastContext.Provider>
  );
}

function Toaster({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  return createPortal(
    // 画面上部中央。pointer-events-none で背後のクリックを透過し、
    // 個々のトーストだけ pointer-events-auto にする。
    <div
      className="pointer-events-none fixed inset-x-0 top-4 z-[100] flex flex-col items-center gap-2 px-4"
      // 支援技術に読み上げさせる。成功/情報は polite、失敗は下の role で assertive。
      aria-live="polite"
      aria-relevant="additions"
    >
      {items.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const Icon = VARIANT_ICON[item.variant];
  return (
    <output
      role={item.variant === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex w-full max-w-sm items-start gap-2 rounded-lg px-3 py-2.5 text-sm shadow-lg',
        VARIANT_CLASSES[item.variant],
        item.leaving
          ? 'animate-out fade-out slide-out-to-top-2 duration-150'
          : 'animate-in fade-in slide-in-from-top-2 duration-200',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span className="flex-1 break-words">{item.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="-mr-1 shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
        aria-label="閉じる"
      >
        <X className="size-4" aria-hidden />
      </button>
    </output>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast は <ToastProvider> の内側で使ってください');
  }
  return ctx;
}
